'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import formatCurrency from '@/utils/format-currency';
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { PriceBreakdownItem } from './price-breakdown-popover';

type PriceBreakdownDetailsModalProps = {
  triggerLabel: string;
  triggerClassName?: string;
  title?: string;
  details: NonNullable<PriceBreakdownItem['details']>;
  total: number;
  description?: string;
  useAccountingFormat?: boolean;
};

const TOOLTIP_WIDTH = 255;
const TOOLTIP_GAP = 8;
const PANEL_WIDTH = 360;

const HtmlInfoTooltip = ({ html }: { html: string }) => {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRect = useCallback(() => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  const show = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    updateRect();
    setOpen(true);
  }, [updateRect]);

  const hide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setOpen(false), 100);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  useLayoutEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    },
    []
  );

  const opensAbove = triggerRect ? triggerRect.top > 180 : true;
  const panelLeft =
    triggerRect &&
    Math.min(
      Math.max(8, triggerRect.left - 12),
      window.innerWidth - TOOLTIP_WIDTH - 8
    );

  const panel =
    open &&
    triggerRect &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="fixed z-[9999] rounded-xl bg-neutral-100 p-2 drop-shadow-lg dark:bg-gray-600"
        style={{
          width: TOOLTIP_WIDTH,
          left: panelLeft ?? undefined,
          ...(opensAbove
            ? { bottom: window.innerHeight - triggerRect.top + TOOLTIP_GAP }
            : { top: triggerRect.bottom + TOOLTIP_GAP }),
        }}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <div
          className="text-sm text-gray-900 dark:text-white"
          dangerouslySetInnerHTML={{
            __html: `<span>${html}</span>`,
          }}
        />
        <svg
          className={clsx(
            'absolute left-3 h-2 text-neutral-100 dark:text-gray-600',
            opensAbove ? 'top-full' : 'bottom-full rotate-180'
          )}
          x="0px"
          y="0px"
          viewBox="0 0 255 255"
        >
          <polygon className="fill-current" points="0,0 127.5,127.5 255,0" />
        </svg>
      </div>,
      document.body
    );

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        className={clsx(
          'cursor-pointer text-gray-7',
          open ? 'text-black-0' : 'hover:text-black-0'
        )}
        viewBox="0 0 256 256"
      >
        <path
          fill="currentColor"
          d="M140 176a4 4 0 0 1-4 4a12 12 0 0 1-12-12v-40a4 4 0 0 0-4-4a4 4 0 0 1 0-8a12 12 0 0 1 12 12v40a4 4 0 0 0 4 4a4 4 0 0 1 4 4m-16-84a8 8 0 1 0-8-8a8 8 0 0 0 8 8m104 36A100 100 0 1 1 128 28a100.11 100.11 0 0 1 100 100m-8 0a92 92 0 1 0-92 92a92.1 92.1 0 0 0 92-92"
        />
      </svg>
      {panel}
    </div>
  );
};

/** Ensures only one price-detail helper is open across the breakdown. */
let activeHelperCloser: (() => void) | null = null;

const ExclusiveHelperRegistrar = ({
  open,
  close,
  triggerRef,
  panelRef,
}: {
  open: boolean;
  close: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
}) => {
  useLayoutEffect(() => {
    if (!open) return;
    if (activeHelperCloser && activeHelperCloser !== close) {
      activeHelperCloser();
    }
    activeHelperCloser = close;
    return () => {
      if (activeHelperCloser === close) {
        activeHelperCloser = null;
      }
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, close, triggerRef, panelRef]);

  return null;
};

export const PriceBreakdownDetailsModal = ({
  triggerLabel,
  triggerClassName,
  title = triggerLabel,
  details,
  total,
  description,
  useAccountingFormat = false,
}: PriceBreakdownDetailsModalProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  return (
    <Popover className="relative inline">
      {({ open, close }) => (
        <>
          <ExclusiveHelperRegistrar
            open={open}
            close={close}
            triggerRef={triggerRef}
            panelRef={panelRef}
          />
          <PopoverButton
            ref={triggerRef}
            type="button"
            className={clsx(
              '!m-0 !inline !h-auto !w-auto !gap-0 !border-0 !bg-transparent !p-0',
              '!font-[inherit] !normal-case !leading-[inherit] !text-inherit',
              'cursor-pointer'
            )}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <span className={clsx('underline', triggerClassName)}>
              {triggerLabel}
            </span>
          </PopoverButton>
          <PopoverPanel
            ref={panelRef}
            as="div"
            transition
            anchor={{ to: 'bottom start', gap: 12, padding: 8 }}
            role="dialog"
            aria-modal="false"
            aria-label={title}
            data-testid="price-breakdown-details-panel"
            className={clsx(
              'group/panel z-[9999] rounded-xl border border-gray-1 bg-white px-4 pb-3 pt-3 text-gray-800 drop-shadow-lg',
              'max-w-[calc(100vw-16px)] transition duration-150 ease-out',
              'data-[closed]:scale-95 data-[closed]:opacity-0'
            )}
            style={{
              width: PANEL_WIDTH,
              maxWidth: `min(${PANEL_WIDTH}px, calc(100vw - 16px))`,
            }}
          >
            <svg
              className={clsx(
                'pointer-events-none absolute left-8 h-4 text-white',
                '-top-4 group-data-[anchor~=top]/panel:-bottom-4',
                'group-data-[anchor~=top]/panel:top-auto',
                'group-data-[anchor~=top]/panel:rotate-180'
              )}
              x="0px"
              y="0px"
              viewBox="0 0 255 255"
              aria-hidden
            >
              <polygon
                className="fill-current"
                points="0,255 127.5,127.5 255,255"
              />
            </svg>
            <CloseButton
              as="button"
              type="button"
              className="absolute right-2 top-2 rounded-full p-1 hover:text-gray-800 hover:shadow"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5 text-gray-900" />
            </CloseButton>
            <div className="flex min-w-0 flex-col gap-2 text-gray-800">
              <h3 className="pr-8 text-xl font-medium leading-tight text-gray-800 dark:text-white">
                {title}
              </h3>
              {details.map((detail, index) => (
                <div
                  key={`${detail.label}-${index}`}
                  className="flex flex-nowrap justify-between gap-4"
                >
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span>{detail.label}</span>
                    {detail.tooltip && <HtmlInfoTooltip html={detail.tooltip} />}
                  </div>
                  <span className="text-end">
                    {detail.lineThroughPrice != null && (
                      <span className="mr-2 text-gray-5 line-through">
                        {formatCurrency(detail.lineThroughPrice, true)}
                      </span>
                    )}
                    {formatCurrency(detail.price, true, useAccountingFormat)}
                  </span>
                </div>
              ))}
              <div className="flex flex-nowrap justify-between gap-4 border-t border-gray-2 pt-2 font-bold">
                <span>{title}</span>
                <span className="text-end">
                  {formatCurrency(total, true, useAccountingFormat)}
                </span>
              </div>
              {description && <p className="text-sm">{description}</p>}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
